import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import { isAfter, isBefore, isSameDay } from 'date-fns';
import { z } from 'zod';

import { labelCollection } from '@/api/collections/labels/label';
import { labelMapCollection } from '@/api/collections/labels/label-map';
import { useLatestBedrijf } from '@/api/queries/bedrijf';
import { Disclaimer } from '@/components/Disclaimer/Disclaimer';
import { DocumentTitle } from '@/components/DocumentTitle';
import { FeedbackMessage } from '@/components/FeedbackMessage';
import { Navbar } from '@/components/Navbar/Navbar';
import { TabsPanel } from '@/components/TabsPanel/TabsPanel';
import { eStrings } from '@/constants/strings';
import { SolidFilter, SolidList } from '@/icons/components';
import { DateUtils } from '@/utils/dateUtils';

import { Body } from '../-components/Body';
import useViewerStore, { SidebarLeftTab } from '../-utils/useViewerStore';
import { viewerRouteApi } from '../-utils/viewerRouteApi';
import { VigerendDataProvider } from './-components/VigerendDataContext';
import { VigerendHeader } from './-components/VigerendHeader';
import { VigerendSidebarFilter } from './-components/VigerendSidebarFilter';
import { VigerendSidebarIndex } from './-components/VigerendSidebarIndex';
import { useFilterVigerendDataByLabels } from './-utils/useFilterVigerendDataByLabels';
import { ensureQueryDataMaxBeginGeldigheid } from './-utils/useMaxBeginGeldigheid';
import { ensureQueryDataMinBeginGeldigheid } from './-utils/useMinBeginGeldigheid';
import { VigerendData, useVigerendData } from './-utils/useVigerendData';

const searchSchema = z.object({
  datum: z.string().date().catch(DateUtils.formatForInput(new Date())),
});

export const Route = createFileRoute('/$bedrijfLineageId/viewer/vigerend')({
  validateSearch: searchSchema.parse,
  loaderDeps: ({ search }) => ({ datum: search.datum }),
  loader: async ({
    params: { bedrijfLineageId },
    context: { queryClient, voorschriftLabelCollection },
    deps: { datum },
  }) => {
    const bedrijf = await queryClient.ensureQueryData(
      useLatestBedrijf.getOptions(bedrijfLineageId)
    );

    const bedrijfId = bedrijf.objects?.[0]?.ID ?? '';
    const searchDate = new Date(datum);

    const [minBeginGeldigheid, maxBeginGeldigheid] = await Promise.all([
      ensureQueryDataMinBeginGeldigheid({ bedrijfId, queryClient }),
      ensureQueryDataMaxBeginGeldigheid({ bedrijfId, queryClient }),
      voorschriftLabelCollection.preload(),
      labelCollection.preload(),
      labelMapCollection.preload(),
    ]);

    if (isBefore(searchDate, minBeginGeldigheid)) {
      FeedbackMessage({
        type: 'info',
        description:
          'De opgegeven datum kan niet worden gebruikt, de datum is aangepast naar een beschikbaar datum.',
      });

      throw redirect({
        to: '/$bedrijfLineageId/viewer/vigerend',
        search: { datum: DateUtils.formatForUrl(minBeginGeldigheid) },
        params: { bedrijfLineageId },
      });
    }

    if (
      isAfter(searchDate, maxBeginGeldigheid) &&
      !isSameDay(searchDate, maxBeginGeldigheid)
    ) {
      FeedbackMessage({
        type: 'info',
        description:
          'De opgegeven datum kan niet worden gebruikt, de datum is aangepast naar een beschikbaar datum.',
      });

      throw redirect({
        to: '/$bedrijfLineageId/viewer/vigerend',
        search: { datum: DateUtils.formatForUrl(maxBeginGeldigheid) },
        params: { bedrijfLineageId },
      });
    }

    await queryClient.ensureQueryData(
      useVigerendData.getOptions({
        date: new Date(datum),
        bedrijfLineageId,
      })
    );
  },
  component: VigerendRouteComponent,
  onLeave: () => {
    useViewerStore.getState().setShowAllMeta(false);
  },
});

const EMPTY_ARRAY: string[] = [];

function VigerendRouteComponent() {
  const { bedrijfLineageId } = Route.useParams();
  const { datum } = Route.useSearch();

  const bedrijf = useLatestBedrijf(bedrijfLineageId);
  const allGrondslagen = useVigerendData({
    date: new Date(datum),
    bedrijfLineageId,
  });

  const labelIds = viewerRouteApi.root.useSearch({
    select: (s) => s.labelIds ?? EMPTY_ARRAY,
  });

  const filteredVigerendData = useFilterVigerendDataByLabels({
    vigerendData: allGrondslagen.data,
    labelIds,
  });

  if (!bedrijf.data || !allGrondslagen.data) return null;

  return (
    <VigerendDataProvider
      allGrondslagen={allGrondslagen.data}
      filteredVigerendData={filteredVigerendData}
    >
      <DocumentTitle
        sections={[
          eStrings.LBL_DIGI_V,
          bedrijf.data.Naam ?? '',
          'Vigerende situatie',
        ]}
      />

      <Navbar />

      <VigerendHeader bedrijf={bedrijf.data} />

      <Body>
        <Body.Sidebar ariaLabel="Vigerende situatie navigatie">
          <Sidebar
            grondslagen={allGrondslagen.data}
            filteredVigerendData={filteredVigerendData}
            bedrijfLineageId={bedrijfLineageId}
            datum={datum}
          />
        </Body.Sidebar>

        <Body.Content>
          <Outlet />
          <Disclaimer className="mt-2 px-6" />
        </Body.Content>
      </Body>
    </VigerendDataProvider>
  );
}

const Sidebar = ({
  grondslagen,
  filteredVigerendData,
  bedrijfLineageId,
  datum,
}: {
  grondslagen: VigerendData;
  filteredVigerendData: VigerendData;
  bedrijfLineageId: string;
  datum: string;
}) => {
  const sidebarTabId = useViewerStore((state) => state.sidebarLeftTabId);
  const setSidebarTabId = useViewerStore((state) => state.setSidebarLeftTabId);

  return (
    <TabsPanel
      value={sidebarTabId}
      onValueChange={(value) => setSidebarTabId(value as SidebarLeftTab)}
    >
      <TabsPanel.Header>
        <TabsPanel.Trigger
          className="h-7"
          value={SidebarLeftTab.INHOUD}
          label="Inhoud"
          icon={SolidList}
        />
        <TabsPanel.Trigger
          className="h-7"
          value={SidebarLeftTab.FILTER}
          label="Filteren"
          icon={SolidFilter}
        />
      </TabsPanel.Header>

      <TabsPanel.Content value={SidebarLeftTab.INHOUD}>
        <VigerendSidebarIndex
          grondslagen={grondslagen}
          bedrijfLineageId={bedrijfLineageId}
          datum={datum}
        />
      </TabsPanel.Content>

      <TabsPanel.Content value={SidebarLeftTab.FILTER}>
        <VigerendSidebarFilter filteredVigerendData={filteredVigerendData} />
      </TabsPanel.Content>
    </TabsPanel>
  );
};
